import { useState, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance';
import RecipeForm from "./RecipeForm";
import SharePopup from "./SharePopup";
import { getApiErrorMessage } from "../lib/apiErrors";
import { deriveUniformTimeFields, toMinutes } from "../lib/recipeTime";

// --- Ingredient scaling helpers ---
const MEASUREMENT_UNITS = [
  "cup", "cups", "tbsp", "tsp", "tablespoon", "tablespoons", "teaspoon", "teaspoons",
  "oz", "ounce", "ounces", "lb", "lbs", "pound", "pounds",
  "g", "kg", "gram", "grams", "kilogram", "kilograms",
  "ml", "l", "liter", "liters", "milliliter", "milliliters",
  "quart", "quarts", "pint", "pints", "gallon", "gallons",
  "pinch", "dash", "handful", "bunch",
];

function parseIngredient(ingredient) {
  const match = ingredient.match(/^(\d+\s+\d+\/\d+|\d+\/\d+|\d+\.?\d*)\s*(.*)/);
  if (!match) return { qty: null, rest: ingredient };
  let qtyStr = match[1];
  let qty;
  if (qtyStr.includes(" ") && qtyStr.includes("/")) {
    const [whole, frac] = qtyStr.split(" ");
    const [num, den] = frac.split("/");
    qty = parseInt(whole) + parseInt(num) / parseInt(den);
  } else if (qtyStr.includes("/")) {
    const [num, den] = qtyStr.split("/");
    qty = parseInt(num) / parseInt(den);
  } else {
    qty = parseFloat(qtyStr);
  }
  return { qty, rest: match[2] };
}

function hasMeasurementUnit(rest) {
  const words = rest.trim().toLowerCase().split(/\s+/);
  return words.some((w) => MEASUREMENT_UNITS.includes(w));
}

function formatQty(qty, rest) {
  // If no measurement unit found, it's a countable item → round up to integer
  if (!hasMeasurementUnit(rest)) return Math.ceil(qty);
  const rounded = Math.round(qty * 100) / 100;
  return rounded === Math.floor(rounded) ? rounded : rounded;
}

function scaleIngredients(ingredients, originalServings, newServings) {
  if (!originalServings || originalServings === newServings) return ingredients;
  const ratio = newServings / originalServings;
  return ingredients.map((ing) => {
    const { qty, rest } = parseIngredient(ing);
    if (qty === null) return ing;
    return `${formatQty(qty * ratio, rest)} ${rest}`;
  });
}

const RecipeEditModal = ({ recipe, onClose, onSave, mode = "edit" }) => {
  const isFork = mode === "fork";
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    ingredients: '',
    instructions: '',
    category: '',
    diet_preference: '',
    prep_time_value: '',
    prep_time_unit: 'min',
    cook_time_value: '',
    cook_time_unit: 'min',
    servings: '',
    difficulty: '',
    visibility: '',
    images: [],
    videos: [],
    tag_names: [],
  });

  const [originalServings] = useState(recipe?.servings || 1);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (recipe) {
      const timeFields = deriveUniformTimeFields(recipe.prep_time, recipe.cook_time);
      setFormData({
        title: isFork ? recipe.title : (recipe.title || ''),
        description: recipe.description || '',
        ingredients: (recipe.recipe_ingredients || []).map(i => i.original_text).join('\n'),
        instructions: recipe.instructions.join('\n'),
        category: recipe.category || '',
        diet_preference: recipe.diet_preference || '',
        ...timeFields,
        servings: recipe.servings || '',
        difficulty: recipe.difficulty || '',
        visibility: recipe.visibility || 'public',
        tag_names: recipe.tags?.map((t) => t.name) || [],
      });
    }
  }, [recipe, isFork]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "tag_names") {
      setFormData((f) => ({ ...f, tag_names: value }));
    } else if (name === "images") {
      setFormData((f) => ({ ...f, images: [...files] }));
    } else if (name === "videos") {
      const valid = [...files].filter((f) => f.size <= 50 * 1024 * 1024);
      setFormData((f) => ({ ...f, videos: valid }));
    } else if (name === "servings" && isFork) {
      const newServings = parseInt(value) || '';
      const baseIngredients = (recipe.recipe_ingredients || []).map(i => i.original_text);
      const scaled = newServings
        ? scaleIngredients(baseIngredients, originalServings, newServings)
        : baseIngredients;
      setFormData((f) => ({ ...f, servings: newServings, ingredients: scaled.join('\n') }));
    } else {
      setFormData((f) => ({ ...f, [name]: value }));
    }
  };

  const sendUpdate = async (extraFields = {}) => {
    const data = new FormData();
    const prepMinutes = toMinutes(formData.prep_time_value, formData.prep_time_unit);
    const cookMinutes = toMinutes(formData.cook_time_value, formData.cook_time_unit);

    Object.entries(formData).forEach(([key, value]) => {
      if (
        key === "prep_time_value" ||
        key === "prep_time_unit" ||
        key === "cook_time_value" ||
        key === "cook_time_unit"
      ) {
        return;
      }

      if (value !== null && value !== undefined && value !== '') {
        if (key === "ingredients" || key === "instructions") {
          data.append(key, JSON.stringify(value.split("\n").filter(Boolean)));
        } else if (key === "images") {
          value.forEach((file) => data.append("images", file));
        } else if (key === "videos") {
          value.forEach((file) => data.append("videos", file));
        } else if (key === "tag_names") {
          value.forEach((tag) => data.append("tag_names", tag));
        } else {
          data.append(key, value);
        }
      }
    });

    if (prepMinutes !== "") {
      data.append("prep_time", prepMinutes);
    }

    if (cookMinutes !== "") {
      data.append("cook_time", cookMinutes);
    }

    Object.entries(extraFields).forEach(([key, value]) => {
      data.append(key, value);
    });
    const res = await axiosInstance.patch(
      `/api/recipes/${recipe.id}/update/`,
      data,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    window.dispatchEvent(new Event("tag-changed"));
    onSave(res.data);
    onClose();
  };

  const handleShareConfirm = async (message) => {
    try {
      await sendUpdate({ share_message: message });
    } catch (err) {
      console.error("Update failed:", err);
      setErrorMsg(getApiErrorMessage(err, "Failed to update recipe."));
    }
    setShowSharePopup(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isFork) {
        const fd = new FormData();
        fd.append("title", formData.title);
        fd.append("description", formData.description);
        fd.append("ingredients", JSON.stringify(formData.ingredients.split("\n").filter(Boolean)));
        fd.append("instructions", JSON.stringify(formData.instructions.split("\n").filter(Boolean)));
        const prepMinutes = toMinutes(formData.prep_time_value, formData.prep_time_unit);
        const cookMinutes = toMinutes(formData.cook_time_value, formData.cook_time_unit);
        if (formData.category) fd.append("category", formData.category);
        if (formData.diet_preference) fd.append("diet_preference", formData.diet_preference);
        if (prepMinutes !== "") fd.append("prep_time", prepMinutes);
        if (cookMinutes !== "") fd.append("cook_time", cookMinutes);
        if (formData.servings) fd.append("servings", formData.servings);
        if (formData.difficulty) fd.append("difficulty", formData.difficulty);
        fd.append("visibility", formData.visibility);
        formData.tag_names.forEach((tag) => fd.append("tag_names", tag));
        if (formData.images?.length) {
          for (const img of formData.images) {
            fd.append("images", img);
          }
        }
        if (formData.videos?.length) {
          for (const vid of formData.videos) {
            fd.append("videos", vid);
          }
        }
        const res = await axiosInstance.post(
          `/api/recipes/${recipe.id}/fork/`,
          fd
        );
        onSave(res.data);
        onClose();
      } else {
        // Check if visibility is changing private → public
        if (recipe.visibility === 'private' && formData.visibility === 'public') {
          setShowSharePopup(true);
          return;
        }
        await sendUpdate();
      }
    } catch (err) {
      console.error(isFork ? "Fork failed:" : "Update failed:", err);
      setErrorMsg(
        getApiErrorMessage(err, isFork ? "Failed to fork recipe." : "Failed to update recipe.")
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto p-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-3xl">
        <div className="flex justify-between items-center p-4 border-b dark:border-gray-700">
          <h2 className="text-xl font-bold dark:text-white">{isFork ? "Fork Recipe" : "Edit Recipe"}</h2>
          <button onClick={onClose} className="dark:text-gray-400 dark:hover:text-white">✕</button>
        </div>
        {isFork && (
          <div className="px-4 pt-3 pb-1 text-sm text-gray-500 dark:text-gray-400">
            Adjust servings to auto-scale ingredient quantities.
          </div>
        )}
        <div className="p-4">
          <RecipeForm formData={formData}
                      onChange={handleChange}
                      onSubmit={handleSubmit}
                      onCancel={onClose}
                      submitButtonText={isFork ? "Fork It!" : "Save Changes"}
                      errorMsg={errorMsg}
          />
        </div>
      </div>

      {showSharePopup && (
        <SharePopup
          onConfirm={handleShareConfirm}
          onCancel={() => setShowSharePopup(false)}
        />
      )}
    </div>
  );
};

export default RecipeEditModal;
